import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  ApiError,
  endpoints,
  type FamilyGroupResponse,
  type FamilyHouseholdResponse,
  type FamilyContextResponse,
  type FamilyHouseholdSummaryResponse,
  type FamilySessionResponse,
} from '@/api'

/**
 * The household the current browser session belongs to (SPEC §11.4).
 *
 * Loaded in one call, because this is the cold-start path: a phone, outdoors,
 * opened from a text message. Everything the first screen needs arrives
 * together rather than as four requests that each wait for the last
 * (SPEC §22.1).
 *
 * A household session identifies a household, not a person, so `isIdentified`
 * is false for most sessions and that is the ordinary state rather than a
 * defect. Anything belonging to one person needs step-up verification
 * (SPEC §14.3.1); this is how a screen knows whether to offer it at all.
 */
export const useFamilyContextStore = defineStore('familyContext', () => {
  const household = ref<FamilyHouseholdResponse | null>(null)
  const group = ref<FamilyGroupResponse | null>(null)
  const session = ref<FamilySessionResponse | null>(null)
  const households = ref<FamilyHouseholdSummaryResponse[]>([])

  /** Whether the session state has actually been determined. */
  const isResolved = ref(false)

  const householdId = computed(() => household.value?.id ?? null)
  const householdName = computed(() => household.value?.name ?? null)
  const groupName = computed(() => group.value?.name ?? null)

  const hasHousehold = computed(() => householdId.value !== null)
  const isIdentified = computed(() => session.value?.level === 'Identified')

  /** More than one household means there is something to switch between. */
  const canSwitchHousehold = computed(() => households.value.length > 1)

  let inFlight: Promise<void> | null = null

  async function ensureLoaded(): Promise<void> {
    if (isResolved.value) return
    if (inFlight) return inFlight

    inFlight = (async () => {
      try {
        const context = await endpoints.familyContext()

        household.value = context.household
        group.value = context.group
        session.value = context.session
        households.value = context.households
        isResolved.value = true
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          clear()
          isResolved.value = true
        }
        // Anything else: leave unresolved. Guessing "no household" on a network
        // blip would send a family who are signed in to the recovery flow, and
        // a car park is exactly where that blip happens.
      } finally {
        inFlight = null
      }
    })()

    return inFlight
  }

  /**
   * Moves this browser into another of the person's households.
   *
   * Re-reads the context afterwards rather than patching the store from the
   * identifier: everything on screen belongs to the household that just
   * changed, and half-updated is worse than a second round trip on an action
   * somebody takes twice a year.
   */
  async function switchHousehold(householdId: string): Promise<void> {
    await endpoints.switchFamilyHousehold(householdId)

    isResolved.value = false
    clear()

    await ensureLoaded()
  }

  /**
   * Adopts a context the server has just returned.
   *
   * Used after an update, which replies with the whole context rather than the
   * changed fields — one shape to read instead of two that can disagree.
   */
  function adopt(context: FamilyContextResponse): void {
    household.value = context.household
    group.value = context.group
    session.value = context.session
    households.value = context.households
    isResolved.value = true
  }

  function clear(): void {
    household.value = null
    group.value = null
    session.value = null
    households.value = []
  }

  return {
    household,
    group,
    session,
    households,
    householdId,
    householdName,
    groupName,
    isResolved,
    hasHousehold,
    isIdentified,
    canSwitchHousehold,
    ensureLoaded,
    switchHousehold,
    adopt,
    clear,
  }
})
